<template>
  <div class="ds-table">
    <table-filter-chips
      v-if="activeFilterChips.length"
      :chips="activeFilterChips"
      @remove="removeFilter"
      @clear-all="clearFilters"
    />

    <div class="ds-table__scroll">
      <table :style="{ minWidth: tableMinWidth }">
        <sortable-table-header
          :columns="columns"
          :filters="filters"
          :sort-column="sortColumn"
          :sort-direction="sortDirection"
          :has-actions="true"
          :actions-width="actionsColumnWidth"
          @toggle-sort="toggleSort"
          @set-filter="setFilter"
          @clear-filters="clearMultipleFilters"
        />
        <tbody>
          <template v-for="item in pagedItems" :key="itemKey(item)">
            <tr>
              <td v-for="col in columns" :key="col.key">
                <template v-if="col.key === 'mediaType'">
                  {{ formatMediaType(item.mediaType) }}
                </template>
                <button
                  v-else-if="col.key === 'blobName'"
                  class="ds-table__filelink"
                  data-qa="media-file-link"
                  @click="$emit('view-file', item)"
                >
                  {{ item.blobName }}
                </button>
                <span v-else class="ds-table__code">{{ item[col.key] }}</span>
              </td>
              <td>
                <div class="ds-table__actions">
                  <button
                    class="ds-icon-button"
                    title="Nieuw bestand uploaden"
                    data-qa="media-upload-button"
                    @click="$emit('upload-request', item)"
                  >
                    <ds-icon name="upload" />
                  </button>
                  <button
                    class="ds-icon-button"
                    :class="{ 'ds-icon-button--active': isExpanded(item) }"
                    :title="isExpanded(item) ? 'Versies verbergen' : 'Versiegeschiedenis'"
                    data-qa="media-versions-button"
                    @click="$emit('toggle-history', item)"
                  >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M3.5 12a8.5 8.5 0 108.5-8.5A8.47 8.47 0 005.7 6.6M3.5 3.6v3.4h3.4"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12 7.8V12l3 1.8"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="isExpanded(item)" class="media-table__history-row">
              <td :colspan="columns.length + 1">
                <media-history-sub-table
                  :loading="historyLoading"
                  :history="history"
                  :media-label="formatMediaType(item.mediaType)"
                  @restore="(versionId) => $emit('restore-version', item, versionId)"
                />
              </td>
            </tr>
          </template>
          <tr v-if="!pagedItems.length">
            <td :colspan="columns.length + 1" class="ds-table__empty">
              Geen resultaten gevonden voor de huidige filters.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ds-pagination
      v-model:page="currentPage"
      v-model:page-size="pageSize"
      :total="filteredItems.length"
    />
  </div>
</template>

<script>
import SortableTableHeader from '@/pages/warningbanner/SortableTableHeader.vue'
import TableFilterChips from '@/pages/warningbanner/TableFilterChips.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsPagination from '@/components/ds/molecules/dsPagination/DsPagination.vue'
import MediaHistorySubTable from './MediaHistorySubTable.vue'
import { formatMediaType } from './mediaConstants'

// Two icon buttons per row.
const ACTIONS_COLUMN_WIDTH = 110

/** Case-insensitive substring match; returns true when filter is empty. */
const matchesText = (value, filter) =>
  !filter ||
  String(value ?? '')
    .toLowerCase()
    .includes(filter.toLowerCase())

/**
 * The media data table, shared by the importer view (four columns) and the
 * dealer view (two columns): the caller passes the column set, this component
 * owns filtering, sorting and pagination. Each row expands into its version
 * history.
 */
export default {
  name: 'MediaTable',
  components: { SortableTableHeader, TableFilterChips, DsIcon, DsPagination, MediaHistorySubTable },
  props: {
    items: { type: Array, required: true },
    columns: { type: Array, required: true },
    expandedItem: { type: String, default: null },
    history: { type: Array, default: () => [] },
    historyLoading: { type: Boolean, default: false },
    defaultSortColumn: { type: String, default: null },
  },
  emits: ['view-file', 'upload-request', 'toggle-history', 'restore-version'],
  data() {
    return {
      filters: Object.fromEntries(this.columns.map((col) => [col.key, ''])),
      sortColumn: this.defaultSortColumn,
      sortDirection: 'asc',
      currentPage: 1,
      pageSize: 25,
    }
  },
  computed: {
    filteredItems() {
      return this.items.filter((item) =>
        this.columns.every((col) => {
          const filter = this.filters[col.key]
          if (!filter) return true
          if (col.filter === 'select') return item[col.key] === filter
          return matchesText(item[col.key], filter)
        })
      )
    },
    activeFilterChips() {
      return this.columns
        .filter((col) => this.filters[col.key])
        .map((col) => ({ key: col.key, label: this.chipLabel(col) }))
    },
    sortedItems() {
      if (!this.sortColumn) return this.filteredItems
      const col = this.sortColumn
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return [...this.filteredItems].sort(
        (a, b) => String(a[col] ?? '').localeCompare(String(b[col] ?? ''), 'nl') * dir
      )
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedItems.slice(start, start + this.pageSize)
    },
    actionsColumnWidth() {
      return `${ACTIONS_COLUMN_WIDTH}px`
    },
    // Sum of the declared column widths plus the actions column. Without a
    // definite table width, `table-layout: fixed` lets cell content inflate the
    // columns instead of the other way round.
    tableMinWidth() {
      const columns = this.columns.reduce((sum, col) => sum + (parseFloat(col.width) || 140), 0)
      return `${columns + ACTIONS_COLUMN_WIDTH}px`
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      },
    },
  },
  methods: {
    formatMediaType,
    itemKey(item) {
      return `${item.dealerId}-${item.establishmentId}-${item.mediaType}`
    },
    isExpanded(item) {
      return this.expandedItem === this.itemKey(item)
    },
    chipLabel(col) {
      const value = this.filters[col.key]
      if (col.filter === 'select') {
        const option = (col.options || []).find((o) => o.value === value)
        return `${col.label}: ${option ? option.label : value}`
      }
      return `${col.label}: "${value}"`
    },
    setFilter(key, value) {
      this.filters[key] = value
    },
    clearMultipleFilters(keys) {
      keys.forEach((key) => {
        this.filters[key] = ''
      })
    },
    removeFilter(key) {
      this.filters[key] = ''
    },
    clearFilters() {
      this.filters = Object.fromEntries(this.columns.map((col) => [col.key, '']))
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        if (this.sortDirection === 'asc') this.sortColumn = null
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The table, buttons and pagination come from the design system
// (styles/ds/). See styles/ds/README.md before adding anything here.

// Version-history expansion row: indented under its parent row, on the same
// wash the parent gets on hover so the pair reads as one unit.
.media-table__history-row td {
  padding: 14px 20px 18px 34px;
  background: var(--ds-color-surface-hover);
}
</style>
